import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { getCurrentTerm } from '@/lib/term';

export interface DashboardStats {
  thisTermAssessments: number;
  totalAssessments: number;
  totalPupils: number;
  yearGroupsCovered: number;
}

/**
 * Fetches the four dashboard stat cards concurrently.
 *
 * @param userId   The authenticated user's UUID.
 * @param isAdmin  True for school_admin / slt — queries school-wide data.
 *                 False for teacher / department_leader — queries own data only.
 */
export function useDashboardStats(userId: string, isAdmin: boolean) {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!userId) {
      setIsLoading(false);
      return;
    }

    const fetchStats = async () => {
      const currentTerm = getCurrentTerm();

      const termQuery = isAdmin
        ? supabase
            .from('saved_assessments')
            .select('*', { count: 'exact', head: true })
            .eq('term', currentTerm)
        : supabase
            .from('saved_assessments')
            .select('*', { count: 'exact', head: true })
            .eq('assessed_by', userId)
            .eq('term', currentTerm);

      const totalQuery = isAdmin
        ? supabase.from('saved_assessments').select('*', { count: 'exact', head: true })
        : supabase
            .from('saved_assessments')
            .select('*', { count: 'exact', head: true })
            .eq('assessed_by', userId);

      const getPupilCount = async (): Promise<number> => {
        if (isAdmin) {
          const { count } = await supabase
            .from('pupils')
            .select('*', { count: 'exact', head: true })
            .eq('is_archived', false);
          return count ?? 0;
        }
        const { data: tcData } = await supabase
          .from('teacher_classes')
          .select('class_id')
          .eq('user_id', userId);
        const classIds = (tcData ?? []).map((tc) => tc.class_id);
        if (classIds.length === 0) return 0;
        const { count } = await supabase
          .from('pupils')
          .select('*', { count: 'exact', head: true })
          .eq('is_archived', false)
          .in('class_id', classIds);
        return count ?? 0;
      };

      const yearGroupsQuery = isAdmin
        ? supabase.from('saved_assessments').select('year_group')
        : supabase
            .from('saved_assessments')
            .select('year_group')
            .eq('assessed_by', userId);

      const [termResult, totalResult, yearGroupsResult, pupilCount] =
        await Promise.all([termQuery, totalQuery, yearGroupsQuery, getPupilCount()]);

      console.log('[useDashboardStats] userId:', userId, 'isAdmin:', isAdmin);
      console.log('[useDashboardStats] termResult:', termResult.count, termResult.error);
      console.log('[useDashboardStats] totalResult:', totalResult.count, totalResult.error);
      console.log('[useDashboardStats] yearGroupsResult:', yearGroupsResult.data, yearGroupsResult.error);

      const yearGroupsCovered = yearGroupsResult.data
        ? new Set(
            yearGroupsResult.data
              .map((r) => r.year_group?.toLowerCase())
              .filter((v): v is string => !!v)
          ).size
        : 0;

      setStats({
        thisTermAssessments: termResult.count ?? 0,
        totalAssessments: totalResult.count ?? 0,
        totalPupils: pupilCount,
        yearGroupsCovered,
      });
      setIsLoading(false);
    };

    fetchStats();
  }, [userId, isAdmin]);

  return { stats, isLoading };
}
